import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Icon, Logo } from "../components/Icon";
import { Page, TopBar } from "../components/Layout";
import { discountNote, Price } from "../components/Price";
import { SubscriptionControls } from "../components/SubscriptionControls";
import { api } from "../lib/api";
import { checkoutClient, goToCheckout, waitForActivation } from "../lib/checkout";
import { load, save } from "../lib/storage";
import { useToast } from "../lib/toast";
import type { Plan, Subscription } from "../lib/types";

interface OmniPlan extends Plan { description: string; ai_tier: string; ai_monthly_usd: number | null }
interface Overview {
  plans: OmniPlan[];
  current: { plan_id: string; name: string } | null;
  subscription: Subscription | null;
  ai_requires_plan: boolean;
}

const PENDING = "omni.checkout.omni";

/** A line at the top of the Marketplace: Omni's own plan (ADR-0033). */
export function OmniBanner() {
  const [o, setO] = useState<Overview | null>(null);
  useEffect(() => {
    api<Overview>("/api/omni/plans").then(setO).catch(() => setO(null));
  }, []);
  if (!o || o.plans.length === 0) return null;
  const cheapest = o.plans.reduce((m, p) => (p.price < m.price ? p : m));
  return (
    <Link to="/marketplace/omni" className="omni-banner">
      <span className="ic-wrap"><Icon name="sparkle" size={24} /></span>
      <span className="grow">
        <strong>{o.current ? `You're on ${o.current.name}` : o.plans.length > 1 ? "Omni plans" : cheapest.name}</strong>
        <span className="sub">{o.current ? "See what's included or change it." : o.ai_requires_plan ? "Turn on AI in every app." : cheapest.description || "More from Omni, in every app."}</span>
        {!o.current && <span style={{ marginTop: 4 }}><Price plan={cheapest} size="sm" prefix={o.plans.length > 1 ? "From" : undefined} /></span>}
      </span>
      <Icon name="chevron-right" size={20} />
    </Link>
  );
}

/** /marketplace/omni: Omni's plans, at this person's prices. */
export function OmniPlans() {
  const nav = useNavigate();
  const toast = useToast();
  const [search] = useSearchParams();
  const [o, setO] = useState<Overview | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(search.get("checkout") === "success");

  const fetchPlans = () => api<Overview>("/api/omni/plans");
  const reload = () => fetchPlans().then(setO).catch((e) => setError((e as Error).message));

  useEffect(() => {
    void reload();
    if (search.get("checkout") === "cancel") toast("Payment cancelled. Nothing was charged.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!waiting) return;
    const buying = load<string | null>(PENDING, null);
    return waitForActivation(async () => {
      const x = await fetchPlans();
      setO(x);
      return !!x.current && (!buying || x.current.plan_id === buying);
    }, () => {
      setWaiting(false);
      save(PENDING, null);
      toast("Your Omni plan is active", "success");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  const subscribe = async (p: OmniPlan) => {
    setBusy(p.id);
    setError(null);
    try {
      const r = await api<Overview & { checkout_url: string | null }>("/api/omni/subscribe", { body: { plan_id: p.id, client: checkoutClient } });
      if (r.checkout_url) {
        save(PENDING, p.id);
        await goToCheckout(r.checkout_url);
        setWaiting(true);
        return;
      }
      setO(r);
      toast(`You're on ${p.name}`, "success");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const sub = o?.subscription;
  const back = (
    <button className="btn btn-ghost btn-sm" style={{ paddingLeft: 4 }} onClick={() => nav("/marketplace")}>
      <Icon name="chevron-left" size={20} />Marketplace
    </button>
  );

  return (
    <>
      <TopBar title="" back={back} avatar={false} />
      <Page>
        <div className="omni-hero">
          <Logo size={32} />
          <h1>{o?.current ? `You're on ${o.current.name}` : "Get more from Omni"}</h1>
          <p>
            {o?.ai_requires_plan
              ? "AI features in every app, like reading receipts and answering questions about your money, come with an Omni plan."
              : "An Omni plan adds more AI to every app in your space."}
          </p>
        </div>
        {error && <p className="error small" role="alert">{error}</p>}
        {waiting && (
          <div className="card checkout-wait" role="status"><span className="spinner" aria-hidden />Waiting for the payment to be confirmed…</div>
        )}
        {o && o.plans.length === 0 && <p className="muted">There are no Omni plans to choose from yet.</p>}

        {o && !o.current && o.plans.length > 0 && (
          <div className="card row between">
            <span><span className="small muted" style={{ display: "block" }}>Your plan</span><strong style={{ fontSize: 17 }}>Free</strong></span>
            <span className="small muted" style={{ textAlign: "right" }}>{o.ai_requires_plan ? "No AI features" : "A small AI allowance"}</span>
          </div>
        )}
        {o?.plans.map((p) => {
          const isCurrent = o.current?.plan_id === p.id;
          return (
            <div key={p.id} className={`card plan-card${isCurrent ? " current" : ""}`}>
              <div className="row between" style={{ alignItems: "flex-start" }}>
                <div className="stack" style={{ gap: 2 }}>
                  <strong style={{ fontSize: 18 }}>{p.name}</strong>
                  {p.description && <span className="small muted">{p.description}</span>}
                </div>
                {isCurrent && <span className="pill ok">Your plan</span>}
              </div>
              <Price plan={p} />
              {discountNote(p) && <span className="small" style={{ color: "var(--omni-accent)" }}>{discountNote(p)}</span>}
              {p.features.length > 0 && (
                <ul>{p.features.map((f) => <li key={f}><Icon name="check" size={18} />{f}</li>)}</ul>
              )}
              {isCurrent ? (
                <SubscriptionControls sub={sub} name={p.name} onChange={() => void reload()} />
              ) : (
                <button className="btn btn-primary btn-block" disabled={busy !== null || waiting} onClick={() => subscribe(p)}>
                  {busy === p.id ? "Working…" : o.current ? `Switch to ${p.name}` : p.trial_days ? `Start ${p.trial_days}-day free trial` : `Get ${p.name}`}
                </button>
              )}
            </div>
          );
        })}

        {o && o.plans.length > 0 && (
          <p className="xs muted">Paid securely with Stripe. Cancel anytime here or in Profile › Subscriptions; your plan runs to the end of the period you paid for.</p>
        )}
      </Page>
    </>
  );
}
